"use client";
import React from "react";
import { motion } from "motion/react";

import { ProjectType } from "@/components/utils/types";

interface ImpactProps {
  project: ProjectType;
}

export const Impact = ({ project }: ImpactProps) => {
  return (
    <section className="relative w-full py-24 md:py-40 px-6 md:px-20 overflow-hidden">
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 100, damping: 20 }}
          className="text-center mb-16 md:mb-20"
        >
          <h2 className="font-recoleta text-4xl md:text-6xl lg:text-7xl text-black dark:text-white">
            The{" "}
            <span className="relative inline-block">
              <span className="bg-linear-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent">
                Impact
              </span>
              <motion.span
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                transition={{
                  type: "spring",
                  stiffness: 100,
                  damping: 20,
                  delay: 0.5,
                }}
                className="absolute -bottom-2 left-0 w-full h-1.5 bg-linear-to-r from-sky-500 to-blue-600 rounded-full origin-left"
              />
            </span>
          </h2>
        </motion.div>

        {/* Impact Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {project.impact?.map((item, idx) => {
            const cardStyles = [
              {
                gradient: "from-sky-500/20 via-sky-500/5 to-transparent",
                border: "border-sky-500/20 hover:border-sky-500/40",
                accent: "bg-sky-500",
                iconBg: "bg-sky-500/10",
                iconText: "text-sky-500 dark:text-sky-400",
                number: "from-sky-400 to-sky-600",
              },
              {
                gradient: "from-blue-500/20 via-blue-500/5 to-transparent",
                border: "border-blue-500/20 hover:border-blue-500/40",
                accent: "bg-blue-500",
                iconBg: "bg-blue-500/10",
                iconText: "text-blue-500 dark:text-blue-400",
                number: "from-blue-400 to-blue-600",
              },
              {
                gradient: "from-indigo-500/20 via-indigo-500/5 to-transparent",
                border: "border-indigo-500/20 hover:border-indigo-500/40",
                accent: "bg-indigo-500",
                iconBg: "bg-indigo-500/10",
                iconText: "text-indigo-500 dark:text-indigo-400",
                number: "from-indigo-400 to-indigo-600",
              },
            ];
            const style = cardStyles[idx] || cardStyles[0];

            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 50, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                transition={{
                  type: "spring",
                  stiffness: 100,
                  damping: 15,
                  delay: idx * 0.15,
                }}
                whileHover={{
                  y: -8,
                  scale: 1.02,
                  transition: { type: "spring", stiffness: 300, damping: 20 },
                }}
                className="group relative"
              >
                {/* Card */}
                <div
                  className={`relative h-full p-8 md:p-10 rounded-3xl border ${style.border} bg-white/70 dark:bg-white/5 backdrop-blur-xl transition-all duration-500 overflow-hidden`}
                >
                  {/* Gradient Background on Hover */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    whileHover={{ opacity: 1 }}
                    className={`absolute inset-0 bg-linear-to-br ${style.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                  />

                  {/* Content */}
                  <div className="relative z-10 flex flex-col gap-6">
                    {/* Text */}
                    <p className="font-outfit text-lg md:text-xl text-black/80 dark:text-white/80 leading-relaxed group-hover:text-black dark:group-hover:text-white transition-colors duration-300">
                      {item}
                    </p>

                    {/* Bottom Decorative Element */}
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: "40%" }}
                      transition={{
                        type: "spring",
                        stiffness: 100,
                        damping: 20,
                        delay: idx * 0.15 + 0.4,
                      }}
                      className={`h-1 ${style.accent} rounded-full opacity-20 group-hover:opacity-40 transition-opacity duration-300`}
                    />
                  </div>

                  {/* Corner Glow Effect */}
                  <div
                    className={`absolute -bottom-20 -right-20 w-40 h-40 ${style.accent} rounded-full blur-3xl opacity-0 group-hover:opacity-20 transition-opacity duration-500`}
                  />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
